import { create } from 'zustand';
import { authService } from '@/services';
import { API_CONFIG } from '@/config/api';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  first_name?: string;
  last_name?: string;
  role: 'customer' | 'provider' | 'admin' | 'support' | 'company';
  profileImage?: string;
  avatar_url?: string;
  providerType?: 'individual' | 'company';
  businessName?: string;
  phone?: string;
  hourlyRate?: number;
  // Company Association Fields
  parentCompanyId?: string;
  isCompanyOwner?: boolean;
  companyRole?: 'owner' | 'manager' | 'technician' | 'apprentice' | 'admin';
  employmentType?: 'employee' | 'contractor' | 'freelancer' | 'partner';
  employmentStatus?: 'active' | 'inactive' | 'suspended' | 'terminated';
  hireDate?: Date;
  terminationDate?: Date;
  commissionRate?: number;
  canTakePersonalJobs?: boolean;
  companyPermissions?: string[];
  // Premium subscription
  isPremium?: boolean;
  premiumExpiryDate?: Date;
  subscriptionTier?: 'PREMIUM' | 'ENTERPRISE';
  emailVerified: boolean;
  phoneVerified: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  _isInitializing: boolean; // Internal flag to prevent concurrent initialization

  // Actions
  login: (credentials: { email: string; password: string }) => Promise<void>;
  loginWithMagicLink: (email: string) => Promise<void>;
  signup: (userData: any) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  clearAuth: () => void;
  upgradeToPremium: (planId: string) => void;
  initialize: () => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<void>;
  syncUserToBackend: (user: User) => Promise<void>;
  seedProviderProfileFromSignup: (user: User) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  _isInitializing: false, // Internal flag to prevent concurrent initialization

  initialize: async () => {
    console.log('🔄 Auth initialize started');

    // Prevent multiple initializations
    if (get().isInitialized) {
      console.log('✅ Already initialized, skipping');
      return;
    }

    // Prevent concurrent initialization calls
    if (get()._isInitializing) {
      console.log('⏳ Already initializing, skipping duplicate call');
      return;
    }

    // Set initializing flag
    set({ _isInitializing: true });

    // Prevent SSR hydration issues - only run on client
    if (typeof window === 'undefined') {
      console.log('⚠️ Server-side rendering, skipping auth initialization');
      return;
    }

    set({ isLoading: true });

    try {
      // Check for existing auth token
      const token = localStorage.getItem('auth_token');
      console.log('🔑 Token from localStorage:', !!token);

      if (!token) {
        console.log('❌ No token found, user not authenticated');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          isInitialized: true
        });
        return;
      }

      // Validate token and get user data with timeout
      console.log('🔍 Validating token with getCurrentUser...');

      try {
        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Auth timeout')), 10000); // 10 second timeout
        });

        const userPromise = authService.getCurrentUser();
        const user = await Promise.race([userPromise, timeoutPromise]);

        console.log('✅ getCurrentUser result:', { success: !!user, hasUser: !!user });

        if (user) {
          // Force role mapping - if role is 'authenticated', default to 'customer'
          let userRole = user.role as string;
          if (userRole === 'authenticated' || !userRole) {
            userRole = 'customer';
            console.log('🔄 Mapped role from "authenticated" to "customer"');
          }

          const mappedUser: User = {
            id: user.id,
            email: user.email,
            firstName: user.first_name || user.email?.split('@')[0] || 'User',
            lastName: user.last_name || '',
            role: userRole as 'customer' | 'provider' | 'admin' | 'support' | 'company',
            profileImage: user.avatar_url,
            providerType: user.provider_type || user.user_metadata?.provider_type,
            businessName: user.business_name
              || user.businessName
              || user.user_metadata?.business_name
              || user.user_metadata?.businessName
              || user.user_metadata?.company_name
              || user.user_metadata?.companyName,
            phone: user.phone,
            emailVerified: user.email_verified || false,
            phoneVerified: user.phone_verified || false,
            createdAt: new Date(user.created_at),
            updatedAt: new Date(user.updated_at || user.created_at),
          };

          console.log('✅ User mapped with role:', mappedUser.role, 'providerType:', mappedUser.providerType);

          set({
            user: mappedUser,
            token,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
            _isInitializing: false
          });

          // Ensure user_id is stored in localStorage for API calls
          localStorage.setItem('user_id', mappedUser.id);

          // Sync user to backend database (optional - don't fail if this fails)
          try {
            await get().syncUserToBackend(mappedUser);
          } catch (syncError) {
            console.warn('⚠️ Failed to sync user to backend during initialization (non-critical):', syncError);
          }

          // Seed provider profile with signup business data if missing
          try {
            await get().seedProviderProfileFromSignup(mappedUser);
          } catch (seedError) {
            console.warn('⚠️ Failed to seed provider profile (non-critical):', seedError);
          }
        } else {
          // Invalid token
          console.log('❌ Invalid token, clearing auth');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_id');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
            _isInitializing: false
          });
        }
      } catch (authError: any) {
        console.error('💥 Auth validation error:', authError);

        // Don't clear tokens on network errors, only on auth errors
        if (authError.message?.includes('Auth timeout') ||
          authError.message?.includes('Network error') ||
          authError.message?.includes('Failed to fetch')) {
          console.log('🌐 Network/timeout error, keeping tokens for retry');
          set({
            user: null,
            token, // Keep token for retry
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
            _isInitializing: false
          });
        } else {
          // Clear tokens only on actual auth errors
          console.log('🔑 Auth error, clearing tokens');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_id');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
            _isInitializing: false
          });
        }
      }
    } catch (error) {
      console.error('💥 Auth initialization error:', error);
      // Clear invalid tokens
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_id');
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
        _isInitializing: false
      });
    }
  },

  login: async (credentials) => {
    console.log('🔐 Login started for:', credentials.email);
    set({ isLoading: true });

    try {
      const result = await authService.login(credentials.email, credentials.password);

      if (!result.success) {
        set({ isLoading: false });
        throw new Error(result.error || 'Authentication failed');
      }

      if (result.user && result.token) {
        // Map user role - preserve provider/admin roles, default others to customer
        let userRole = result.user.role as string;
        
        // Only default to customer if role is explicitly 'authenticated' or missing
        // Preserve 'provider', 'company', 'admin', 'support' roles
        if (userRole === 'authenticated' || !userRole) {
          userRole = 'customer';
          console.log('🔄 Login: Mapped role from "authenticated" to "customer"');
        } else {
          console.log('🔄 Login: Preserving role:', userRole);
        }

        const user: User = {
          id: result.user.id,
          email: result.user.email,
          firstName: result.user.first_name || result.user.email?.split('@')[0] || 'User',
          lastName: result.user.last_name || '',
          role: userRole as 'customer' | 'provider' | 'admin' | 'support' | 'company',
          profileImage: result.user.avatar_url,
          providerType: result.user.provider_type || result.user.user_metadata?.provider_type,
          businessName: result.user.business_name
            || result.user.businessName
            || result.user.user_metadata?.business_name
            || result.user.user_metadata?.businessName
            || result.user.user_metadata?.company_name
            || result.user.user_metadata?.companyName,
          phone: result.user.phone,
          emailVerified: result.user.email_verified || false,
          phoneVerified: result.user.phone_verified || false,
          createdAt: new Date(result.user.created_at),
          updatedAt: new Date(result.user.updated_at || result.user.created_at),
        };

        console.log('✅ Login: User mapped with role:', user.role, 'providerType:', user.providerType);

        // Store auth data
        localStorage.setItem('auth_token', result.token);
        localStorage.setItem('user_id', user.id);

        set({
          user,
          token: result.token,
          isAuthenticated: true,
          isLoading: false,
          isInitialized: true
        });

        console.log('✅ Login successful - State updated:', {
          email: user.email,
          role: user.role,
          providerType: user.providerType,
          isAuthenticated: true,
          isInitialized: true
        });

        // Sync user to backend database (optional - don't fail login if this fails)
        try {
          await get().syncUserToBackend(user);
        } catch (syncError) {
          console.warn('⚠️ Failed to sync user to backend during login (non-critical):', syncError);
        }

        // Seed provider profile with signup business data if missing
        try {
          await get().seedProviderProfileFromSignup(user);
        } catch (seedError) {
          console.warn('⚠️ Failed to seed provider profile (non-critical):', seedError);
        }
      } else {
        set({ isLoading: false });
        throw new Error('Invalid authentication response');
      }
    } catch (error) {
      set({ isLoading: false });
      console.error('❌ Login failed:', error);
      throw error;
    }
  },

  loginWithMagicLink: async (email: string) => {
    try {
      const result = await authService.loginWithMagicLink(email);
      if (!result.success) {
        throw new Error(result.error || 'Magic link failed');
      }
    } catch (error) {
      throw error;
    }
  },

  signup: async (userData) => {
    console.log('📝 Signup started for:', userData.email);
    set({ isLoading: true });

    try {
      const result = await authService.signup(userData);

      if (!result.success) {
        set({ isLoading: false });
        throw new Error(result.error || 'Registration failed');
      }

      console.log('✅ User registered successfully');
      set({ isLoading: false });

      // Note: User will need to verify email before they can login
    } catch (error) {
      set({ isLoading: false });
      console.error('❌ Signup failed:', error);
      throw error;
    }
  },

  logout: async () => {
    console.log('🚪 Logout started');

    // Clear local state and storage immediately
    console.log('🧹 Clearing auth state and localStorage immediately');

    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_id');
    }

    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: true // Keep initialized state
    });

    // Call auth service logout in background (don't wait for it)
    authService.logout().catch(error => {
      console.warn('⚠️ AuthService logout error (non-critical):', error);
    });

    console.log('✅ Logout completed');
  },

  updateUser: async (updates: Partial<User>) => {
    const { user } = get();
    if (user) {
      // If updating role and current role is 'authenticated', force to 'customer'
      if ((updates.role as string) === 'authenticated' || (user.role as string) === 'authenticated') {
        updates.role = 'customer';
        console.log('🔄 UpdateUser: Forced role from "authenticated" to "customer"');
      }

      const updatedUser = { ...user, ...updates, updatedAt: new Date() };
      set({ user: updatedUser });
    }
  },

  setUser: (user) => {
    set({ user, isAuthenticated: true, isInitialized: true });
  },

  setToken: (token) => {
    set({ token });
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth_token', token);
    }
  },

  clearAuth: () => {
    console.log('🧹 Clearing auth state');
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_id');
    }
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: true
    });
  },

  syncUserToBackend: async (user: User) => {
    try {
      console.log('🔄 Starting user sync to backend...', { userId: user.id, email: user.email });

      const token = get().token;
      if (!token) {
        console.warn('⚠️ No authentication token available for user sync - skipping');
        return;
      }

      // Force role mapping for sync
      let syncRole = user.role as string;
      if (syncRole === 'authenticated' || !syncRole) {
        syncRole = 'customer';
        console.log('🔄 Sync: Mapped role from "authenticated" to "customer"');
      }

      const syncData = {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        role: syncRole,
        emailVerified: user.emailVerified,
        phoneVerified: user.phoneVerified,
      };

      console.log('📤 Sending user sync data:', syncData);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || API_CONFIG.BASE_URL}/api/v1/auth/users/sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Origin': window.location.origin,
          },
          body: JSON.stringify(syncData),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        console.log('📥 User sync response:', { status: response.status, statusText: response.statusText });

        if (!response.ok) {
          // Silently fail - this is optional background sync
          if (response.status === 404) {
            console.log('ℹ️ User sync endpoint not available (404) - skipping');
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.warn('⚠️ User sync failed (non-critical):', { status: response.status, errorData });
          }
          return;
        }

        const responseData = await response.json();
        console.log('✅ User synced to backend database successfully:', responseData);
      } catch (fetchError: any) {
        clearTimeout(timeoutId);

        if (fetchError.name === 'AbortError') {
          console.warn('⚠️ User sync timed out - continuing without backend sync');
        } else {
          console.warn('⚠️ User sync failed (non-critical):', fetchError.message);
        }
      }
    } catch (error) {
      console.warn('⚠️ User sync failed (non-critical):', error);
    }
  },

  seedProviderProfileFromSignup: async (user: User) => {
    try {
      if (!user || (user.role !== 'provider' && user.role !== 'company')) {
        return;
      }

      const token = get().token;
      if (!token) {
        return;
      }

      if (!user.businessName) {
        return;
      }

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || API_CONFIG.BASE_URL;
      const profileResponse = await fetch(`${apiBaseUrl}/api/v1/auth/providers/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!profileResponse.ok) {
        return;
      }

      const profilePayload = await profileResponse.json();
      const profileData = profilePayload.profile_data || {};
      const hasBusinessName = profileData.businessName || profileData.business_name;

      if (hasBusinessName) {
        return;
      }

      const mergedProfile = { ...profileData };
      if (!mergedProfile.firstName && user.firstName) mergedProfile.firstName = user.firstName;
      if (!mergedProfile.lastName && user.lastName) mergedProfile.lastName = user.lastName;
      if (!mergedProfile.email && user.email) mergedProfile.email = user.email;
      if (!mergedProfile.phone && user.phone) mergedProfile.phone = user.phone;
      if (!mergedProfile.businessName && user.businessName) mergedProfile.businessName = user.businessName;
      if (!mergedProfile.providerType && user.providerType) mergedProfile.providerType = user.providerType;

      await fetch(`${apiBaseUrl}/api/v1/auth/providers/profile/wizard`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_step: profilePayload.current_step || 1,
          completed_steps: profilePayload.completed_steps || [],
          profile_data: mergedProfile,
          status: profilePayload.status || 'draft'
        })
      });
    } catch (err) {
      console.warn('Failed to seed provider profile from signup:', err);
    }
  },

  refreshUser: async () => {
    console.log('🔄 Refreshing user data...');
    const { initialize } = get();

    // Force re-initialization to get updated user data
    set({ isInitialized: false });
    await initialize();

    console.log('✅ User data refreshed');
  },

  upgradeToPremium: (planId: string) => {
    set((state) => {
      if (!state.user) return state;

      const expiryDate = new Date();
      expiryDate.setMonth(expiryDate.getMonth() + 1);

      const updatedUser = {
        ...state.user,
        isPremium: true,
        premiumExpiryDate: expiryDate,
        subscriptionTier: planId.toUpperCase() as 'PREMIUM' | 'ENTERPRISE'
      };

      return {
        ...state,
        user: updatedUser
      };
    });
  }
}));